import { Ceil, POS_INFINITY } from "@pdfking/math"
import { Constant } from "@pdfking/tfunc"

import type { CalendarLayout, CalendarPageKind, PageContext } from "#internal"

import { AgendaWeeks } from "../agenda/AgendaWeeks"

// The pages the months take: one each, one each and their pictures', or one;
// a week's a page each, a pad's every page
const MONTH_PAGES: Record<CalendarLayout, (year: number) => number> = {
  month: Constant(12),
  pad: Constant(POS_INFINITY),
  spread: Constant(24),
  week: year => AgendaWeeks(year).weeks,
  year: Constant(1),
}

/** What page `page_number` of `total_pages` of a calendar laid out as
 * `pages` shows, for `year`. A cover's every page, a Copertina's or a
 * Basetta's, the same. A pad's page is its sheet's, front and rear alike.
 * Any other page is counted among the pages printed, the fronts alone on
 * `one_sided` sheets: as many as its months take or fewer, January's first;
 * with more, the cover first. A month a page with room for a picture before
 * each month is a spread: each month on a page, its picture on the one
 * before it. Past the months, the last page is the back cover, any other
 * notes. */
export const CalendarPagePlan = (
  pages: CalendarLayout,
  {
    is_cover,
    one_sided,
    page_number,
    total_pages,
    year,
  }: Pick<PageContext, "is_cover" | "one_sided" | "page_number" | "total_pages" | "year">
): CalendarPageKind => {
  if (is_cover) return { enum: "case" }
  // Printed on one side or both
  if (pages === "pad") return { enum: "sheet", sheet: Ceil(page_number / 2) }
  // The page's place among those printed, and how many are
  const n = one_sided ? (page_number + 1) / 2 : page_number
  const printed = one_sided ? total_pages / 2 : total_pages
  const layout = pages === "month" && printed >= MONTH_PAGES.spread(year) ? "spread" : pages
  const months = MONTH_PAGES[layout](year)
  // A planner's pages are its weeks, from the first: no cover among them
  const cover = printed > months && layout !== "week" ? 1 : 0
  if (cover && n === 1) return { enum: "cover" }
  const after = n - cover
  if (after > months) return n === printed ? { enum: "back" } : { enum: "notes" }
  if (layout === "week") return { enum: "week", week: after }
  if (layout === "year") return { enum: "year" }
  if (layout !== "spread") return { enum: "month", month: after }
  // A spread's months on its even places from the cover's, their pictures on
  // the odd ones: with no cover, January's comes first
  const place = after + 1 - cover
  return place % 2
    ? { enum: "picture", month: (place + 1) / 2 }
    : { enum: "month", month: place / 2 }
}
